import { PICKUP_CONSENT_ENABLED } from "../constants/features.js";
import type { VisibleSections } from "../validation/sharedLink.schema.js";

/**
 * A link's sections as it opens them TODAY. `pickup` is optional here, unlike
 * in the stored VisibleSections: it is absent while PICKUP_CONSENT_ENABLED is
 * off (and on links issued before the key existed), so a reader must treat a
 * missing key as closed. Test it with `=== true` or plain truthiness, never
 * `!== false`, which would fail open.
 */
export type EffectiveVisibleSections = Omit<VisibleSections, "pickup"> & { pickup?: boolean };

/**
 * The sections a share link actually opens TODAY, given the feature switches.
 *
 * While PICKUP_CONSENT_ENABLED is off the `pickup` key is dropped (absent, not
 * set to false, exactly like a link issued before the key existed), so every
 * reader that tests `sections.pickup` stays closed, including a link stored
 * with `pickup: true`. The result is typed with `pickup` optional, so callers
 * cannot assume it is there. The stored JSONB is never rewritten: flipping the
 * switch back reopens exactly the links that were issued with it.
 *
 * Used by the public resolve (scopeStripper), the parent's link lists and the
 * demo mocks, so the sections a parent sees listed are the ones a caregiver
 * can open.
 */
export function effectiveVisibleSections<T extends { pickup?: boolean }>(
  sections: T,
): Omit<T, "pickup"> & { pickup?: boolean } {
  if (PICKUP_CONSENT_ENABLED || !("pickup" in sections)) return sections;
  const { pickup: _pickup, ...open } = sections;
  return open;
}
